import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaPaperPlane, FaCheckCircle, FaExclamationTriangle, FaEnvelope, FaPhone, FaLinkedin, FaInstagram, FaFacebookF, FaWhatsapp, FaMapMarkerAlt } from 'react-icons/fa';
import { sendContactEmail } from '../services/emailService';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const emailResult = await sendContactEmail(formData);
      
      if (emailResult.success) {
        setSubmitStatus('success');
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
        
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2500);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-gradient-to-br from-[#22446b]/90 via-[#2d5a8a]/90 to-[#338edc]/90 backdrop-blur-sm"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="relative bg-white rounded-lg sm:rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
          >
            {/* Close Button */}
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute top-3 right-3 sm:top-6 sm:right-6 w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-[#338edc] text-gray-700 hover:text-white rounded-full flex items-center justify-center transition-all z-10"
            >
              <FaTimes className="text-base sm:text-lg" />
            </motion.button>

            <div className="flex flex-col md:flex-row max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
              {/* Left Sidebar - Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-[#22446b] via-[#2d5a8a] to-[#338edc] text-white p-6 sm:p-8 md:p-10 md:w-2/5 relative md:overflow-y-auto flex-shrink-0"
              >
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-purple-600/20 to-pink-600/20 rounded-full blur-3xl"></div>
                
                {/* Subtle grid pattern */}
                <div className="absolute inset-0 opacity-5" style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
                  backgroundSize: '50px 50px'
                }}></div>

                <div className="relative z-10">
                  {/* Header Section */}
                  <div className="mb-4 md:mb-10 pb-4 md:pb-6 md:border-b border-white/10">
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 font-rajdhani text-white tracking-tight">
                        Get In Touch
                      </h3>
                      <div className="w-16 h-1 bg-gradient-to-r from-white/80 to-white/40 rounded-full mb-3 md:mb-4"></div>
                      <p className="text-sm sm:text-base text-gray-300 font-opensans leading-relaxed hidden md:block">
                        We're here to help and answer any question you might have. We look forward to hearing from you.
                      </p>
                    </motion.div>
                  </div>

                  {/* Social Links - Mobile (shown right after header) */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="md:hidden"
                  >
                    <div className="flex gap-3 justify-center">
                      <motion.a
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        href="https://www.facebook.com/share/1GLCUxZKbX/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-gradient-to-br from-[#1877F2] to-[#0d5dbf] rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300 border border-white/10"
                        aria-label="Facebook"
                      >
                        <FaFacebookF className="text-base" />
                      </motion.a>
                      <motion.a
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        href="https://www.linkedin.com/company/sutra-sync-technology"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-gradient-to-br from-[#0A66C2] to-[#004182] rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-600/30 hover:shadow-blue-600/50 transition-all duration-300 border border-white/10"
                        aria-label="LinkedIn"
                      >
                        <FaLinkedin className="text-base" />
                      </motion.a>
                      <motion.a
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        href="https://www.instagram.com/sutrasync_techonologies/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] rounded-xl flex items-center justify-center text-white shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 transition-all duration-300 border border-white/10"
                        aria-label="Instagram"
                      >
                        <FaInstagram className="text-base" />
                      </motion.a>
                      <motion.a
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        href="https://wa.me/917483961283"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-gradient-to-br from-[#25D366] to-[#1da851] rounded-xl flex items-center justify-center text-white shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-all duration-300 border border-white/10"
                        aria-label="WhatsApp"
                      >
                        <FaWhatsapp className="text-lg" />
                      </motion.a>
                    </div>
                  </motion.div>

                  {/* Contact Details - Hidden on mobile */}
                  <div className="space-y-5 sm:space-y-6 hidden md:block mb-8">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-4 group cursor-pointer"
                    >
                      <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 group-hover:border-white/40 transition-all duration-300 flex-shrink-0">
                        <FaPhone className="text-white text-base" />
                      </div>
                      <div>
                        <p className="text-xs text-white/70 font-opensans mb-1 uppercase tracking-wider">Phone Number</p>
                        <a href="tel:+917483961283" className="text-base text-white font-semibold hover:text-white/80 transition-colors">+91 7483961283</a>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-4 group cursor-pointer"
                    >
                      <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 group-hover:border-white/40 transition-all duration-300 flex-shrink-0">
                        <FaEnvelope className="text-white text-base" />
                      </div>
                      <div>
                        <p className="text-xs text-white/70 font-opensans mb-1 uppercase tracking-wider">Email Address</p>
                        <a href="mailto:info@sutrasync.com" className="text-base text-white font-semibold hover:text-white/80 transition-colors break-all">info@sutrasync.com</a>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 }}
                      whileHover={{ x: 5 }}
                      className="flex items-start gap-4 group cursor-pointer"
                    >
                      <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 group-hover:border-white/40 transition-all duration-300 flex-shrink-0">
                        <FaMapMarkerAlt className="text-white text-base" />
                      </div>
                      <div>
                        <p className="text-xs text-white/70 font-opensans mb-1 uppercase tracking-wider">Headquarters</p>
                        <p className="text-sm text-white/90 font-opensans leading-relaxed">Scintilla Apartment, 23, 1st Main Rd, 4th Phase, Yelahanka New Town, Bengaluru, Karnataka 560064</p>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 }}
                      whileHover={{ x: 5 }}
                      className="flex items-start gap-4 group cursor-pointer"
                    >
                      <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 group-hover:border-white/40 transition-all duration-300 flex-shrink-0">
                        <FaMapMarkerAlt className="text-white text-base" />
                      </div>
                      <div>
                        <p className="text-xs text-white/70 font-opensans mb-1 uppercase tracking-wider">Branch Office</p>
                        <p className="text-sm text-white/90 font-opensans leading-relaxed">402, 4th Floor, Global Business Park, Mehrauli-Gurgaon Rd, Sikanderpur, Sector 26, Gurugram, Haryana 122002</p>
                      </div>
                    </motion.div>
                  </div>

                  {/* Social Links - Desktop */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="pt-6 border-t border-white/10 hidden md:block"
                  >
                    <p className="text-sm text-gray-400 mb-4 font-opensans font-semibold uppercase tracking-wider">
                      Connect With Us
                    </p>
                    <div className="flex gap-3">
                      <motion.a
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        href="https://www.facebook.com/share/1GLCUxZKbX/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-gradient-to-br from-[#1877F2] to-[#0d5dbf] rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300 border border-white/10"
                        aria-label="Facebook"
                      >
                        <FaFacebookF className="text-base" />
                      </motion.a>
                      <motion.a
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        href="https://www.linkedin.com/company/sutra-sync-technology"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-gradient-to-br from-[#0A66C2] to-[#004182] rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-600/30 hover:shadow-blue-600/50 transition-all duration-300 border border-white/10"
                        aria-label="LinkedIn"
                      >
                        <FaLinkedin className="text-base" />
                      </motion.a>
                      <motion.a
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        href="https://www.instagram.com/sutrasync_techonologies/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] rounded-xl flex items-center justify-center text-white shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 transition-all duration-300 border border-white/10"
                        aria-label="Instagram"
                      >
                        <FaInstagram className="text-base" />
                      </motion.a>
                      <motion.a
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        href="https://wa.me/917483961283"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-gradient-to-br from-[#25D366] to-[#1da851] rounded-xl flex items-center justify-center text-white shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-all duration-300 border border-white/10"
                        aria-label="WhatsApp"
                      >
                        <FaWhatsapp className="text-lg" />
                      </motion.a>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Right Side - Form */}
              <div className="p-6 sm:p-8 md:p-10 md:w-3/5 bg-gray-50 overflow-y-auto">
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-6 sm:mb-8"
                >
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#22446b] mb-2 font-rajdhani">
                    Contact Us
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 font-opensans">
                    Any question or remarks? Just write us a message!
                  </p>
                </motion.div>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 pb-6 sm:pb-8">
                  {/* Name Fields Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <label htmlFor="modal-name" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 font-rajdhani">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="modal-name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-b-2 border-gray-300 focus:border-black bg-transparent focus:outline-none transition-all font-opensans"
                        placeholder="Your First Name"
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <label htmlFor="modal-lastname" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 font-rajdhani">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="modal-lastname"
                        name="lastname"
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-b-2 border-gray-300 focus:border-black bg-transparent focus:outline-none transition-all font-opensans"
                        placeholder="Last Name"
                      />
                    </motion.div>
                  </div>

                  {/* Email and Phone Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <label htmlFor="modal-email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 font-rajdhani">
                        Mail
                      </label>
                      <input
                        type="email"
                        id="modal-email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-b-2 border-gray-300 focus:border-black bg-transparent focus:outline-none transition-all font-opensans"
                        placeholder="example@gmail.com"
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <label htmlFor="modal-phone" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 font-rajdhani">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="modal-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-b-2 border-gray-300 focus:border-black bg-transparent focus:outline-none transition-all font-opensans"
                        placeholder="+91 987 654 3210"
                      />
                    </motion.div>
                  </div>

                  {/* Service Selection */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3 font-rajdhani">
                      What type of service do you need?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      {['Web & App Development', 'Graphic Design', 'Digital Marketing', 'Other'].map((service, index) => (
                        <label
                          key={index}
                          className="flex items-center gap-2 cursor-pointer group"
                        >
                          <input
                            type="radio"
                            name="service"
                            value={service}
                            checked={formData.service === service}
                            onChange={handleChange}
                            className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#106bcb] border-gray-300 focus:ring-[#106bcb] flex-shrink-0"
                          />
                          <span className="text-xs sm:text-sm text-gray-700 font-opensans group-hover:text-[#106bcb] transition-colors">
                            {service}
                          </span>
                        </label>
                      ))}
                    </div>
                  </motion.div>

                  {/* Message Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <label htmlFor="modal-message" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 font-rajdhani">
                      Message
                    </label>
                    <textarea
                      id="modal-message"
                      name="message"
                      required
                      rows={3}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-b-2 border-gray-300 focus:border-black bg-transparent focus:outline-none transition-all font-opensans resize-none"
                      placeholder="Write your message..."
                    ></textarea>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="flex justify-end pt-2 sm:pt-4"
                  >
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                      disabled={isSubmitting}
                      className={`font-bold px-6 sm:px-10 py-3 sm:py-4 transition-all duration-300 flex items-center justify-center gap-2 rounded-lg font-rajdhani text-sm sm:text-base ${
                        isSubmitting 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-[#22446b] to-[#338edc] hover:from-[#338edc] hover:to-[#22446b] text-white shadow-lg hover:shadow-xl'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full"
                          />
                          <span className="text-sm sm:text-base">Sending...</span>
                        </>
                      ) : (
                        <>
                          <span className="text-sm sm:text-base">Send Message</span>
                          <FaPaperPlane className="text-xs sm:text-sm" />
                        </>
                      )}
                    </motion.button>
                  </motion.div>

                  {/* Status Messages */}
                  <AnimatePresence>
                    {submitStatus === 'success' && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 sm:gap-3"
                      >
                        <FaCheckCircle className="text-green-500 text-base sm:text-xl flex-shrink-0" />
                        <div>
                          <p className="text-green-800 font-semibold font-rajdhani text-sm sm:text-base">Message sent successfully!</p>
                          <p className="text-green-600 text-xs sm:text-sm font-opensans">We'll get back to you soon.</p>
                        </div>
                      </motion.div>
                    )}

                    {submitStatus === 'error' && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 sm:gap-3"
                      >
                        <FaExclamationTriangle className="text-red-500 text-base sm:text-xl flex-shrink-0" />
                        <div>
                          <p className="text-red-800 font-semibold font-rajdhani text-sm sm:text-base">Failed to send message</p>
                          <p className="text-red-600 text-xs sm:text-sm font-opensans">Please try again later.</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
